import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Sales, Tasks, Masters, Accounts, EarlyDelivery } from "../lib/api";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Banknote, AlertTriangle, CheckCircle2, ArrowRight, Search, Wallet, Truck,
  AlertOctagon, FileText, Phone, Bike, MapPin, User, Clock, MoreVertical,
  IndianRupee, ChevronRight, RotateCcw, ArrowLeft,
} from "lucide-react";
import { cn } from "../lib/utils";
import { fmtDate, fmtDateTime } from "../lib/auditFormat";
import ExportButton from "../components/ExportButton";
import { defaultFilters, buildApiParams } from "../lib/filters";
import EarlyDeliveryApprovalsPanel from "./EarlyDeliveryApprovalsPanel";
import { TransitHoldList } from "../components/TransitHoldList";

// ─────────── helpers ───────────

const today = () => new Date().toISOString().slice(0, 10);

function fmtAge(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "0m";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

const DATE_CHIPS = [
  { value: "all",   label: "All" },
  { value: "today", label: "Today" },
  { value: "week",  label: "This Week" },
  { value: "month", label: "This Month" },
];

const SLA_OPTIONS = [
  { value: "all",      label: "All SLA" },
  { value: "on_track", label: "On Track" },
  { value: "at_risk",  label: "At Risk" },
  { value: "delayed",  label: "Delayed" },
  { value: "paused",   label: "Paused" },
];

const STATUS_TABS = [
  { value: "pending",      label: "Pending" },
  { value: "active",       label: "Active" },
  { value: "on_hold",      label: "On Hold" },
  { value: "delayed",      label: "Delayed" },
  { value: "transit_hold", label: "Transit Hold" },
  { value: "completed",    label: "Completed" },
];

export default function AccountsQueue() {
  const { activeLocationIds, locations, activeUser, users } = useApp();
  const navigate = useNavigate();
  const locParam = useMemo(() => activeLocationIds.join(","), [activeLocationIds]);
  const locMap = Object.fromEntries(locations.map((l) => [l.id, l]));
  const userMap = Object.fromEntries((users || []).map((u) => [u.id, u]));

  // ─── server data
  const [stats, setStats] = useState({ pending: 0, completed_today: 0, delayed: 0 });
  const [rows, setRows] = useState([]);
  const [financeMasters, setFinanceMasters] = useState([]);
  const [edPendingCount, setEdPendingCount] = useState(0);

  // ─── client filters
  const [section, setSection] = useState("invoices");  // "invoices" | "early_delivery"
  const [statusTab, setStatusTab] = useState("pending");
  const [dateChip, setDateChip] = useState("all");
  const [search, setSearch] = useState("");
  const [locFilter, setLocFilter] = useState("all");
  const [buyMode, setBuyMode] = useState("all");
  const [slaFilter, setSlaFilter] = useState("all");
  const [filters] = useState(defaultFilters());

  // Tab counts (computed once rows are loaded — they reflect the current
  // search/location/date scope so the user sees how many are in each bucket).
  const [tabCounts, setTabCounts] = useState({});

  useEffect(() => {
    (async () => setFinanceMasters(await Masters.list({ type: "finance_company" })))();
  }, []);

  // Keep Early Delivery pending count fresh
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const data = await EarlyDelivery.queue({
          status: "pending",
          location_ids: locParam || undefined,
        });
        if (!cancelled) setEdPendingCount(data?.pending_count || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const timer = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [locParam]);

  // Main fetch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await Accounts.stats({ location_ids: locParam });
      if (cancelled) return;
      setStats(s);

      // Transit Hold tab is rendered by <TransitHoldList/> which fetches its
      // own data. Skip the task-based fetch to avoid noise / stale rows.
      if (statusTab === "transit_hold") {
        setRows([]);
      } else {
      // Map the synthetic client tabs back to the server's pending/completed.
      const serverStatus = ["on_hold", "active", "delayed"].includes(statusTab)
        ? "pending"
        : statusTab;
      const tasks = await Tasks.list({
        type: "ACCOUNTS_INVOICE",
        status: serverStatus,
        location_ids: locFilter === "all" ? locParam : locFilter,
        with_sla: "true",
        ...buildApiParams(filters),
      });
      if (cancelled) return;
      const saleIds = [...new Set(tasks.map((t) => t.sale_id))];
      const sales = saleIds.length ? await Sales.byIds(saleIds) : [];
      if (cancelled) return;
      const saleBy = Object.fromEntries(sales.map((s) => [s.id, s]));
      let merged = tasks
        .map((t) => ({ task: t, sale: saleBy[t.sale_id] }))
        .filter((x) => x.sale);

      // Per-tab client filter
      const td = today();
      if (statusTab === "on_hold")  merged = merged.filter((x) => x.task.sla_paused);
      if (statusTab === "active")   merged = merged.filter((x) => !x.task.sla_paused);
      if (statusTab === "delayed")  merged = merged.filter((x) =>
        x.sale.delivery?.promised_date && x.sale.delivery.promised_date < td);

      // Date chip — currently scopes to created_at / completed_at
      if (dateChip !== "all") {
        const now = new Date();
        const startOf = (() => {
          if (dateChip === "today") return new Date(now.getFullYear(), now.getMonth(), now.getDate());
          if (dateChip === "week") {
            const d = new Date(now); d.setDate(d.getDate() - d.getDay()); d.setHours(0,0,0,0);
            return d;
          }
          if (dateChip === "month") return new Date(now.getFullYear(), now.getMonth(), 1);
          return null;
        })();
        if (startOf) {
          merged = merged.filter((x) => {
            const ref = statusTab === "completed"
              ? x.task.completed_at
              : x.task.created_at;
            if (!ref) return false;
            return new Date(ref) >= startOf;
          });
        }
      }

      // Buy mode
      if (buyMode !== "all") {
        merged = merged.filter((x) =>
          (x.sale.finance?.buy_mode || "cash").toLowerCase() === buyMode);
      }

      // SLA filter
      if (slaFilter !== "all") {
        merged = merged.filter((x) => {
          if (slaFilter === "paused") return !!x.task.sla_paused;
          return x.task.sla?.status === slaFilter && !x.task.sla_paused;
        });
      }

      // Search
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        merged = merged.filter((x) =>
          (x.sale.sr_number || "").toLowerCase().includes(q) ||
          (x.sale.customer?.name || "").toLowerCase().includes(q) ||
          (x.sale.customer?.phone || "").includes(q) ||
          (x.sale.vehicle?.vin || "").toLowerCase().includes(q) ||
          (x.sale.road_tax?.regn_no || "").toLowerCase().includes(q),
        );
      }

      // Sort: delayed (overdue & pending) first, then by promised date asc
      merged.sort((a, b) => {
        const aDel = a.task.status === "pending" && (a.sale.delivery?.promised_date || "") < td;
        const bDel = b.task.status === "pending" && (b.sale.delivery?.promised_date || "") < td;
        if (aDel !== bDel) return aDel ? -1 : 1;
        return (a.sale.delivery?.promised_date || "")
          .localeCompare(b.sale.delivery?.promised_date || "");
      });
      setRows(merged);
      }

      // Tab counts (informational, server-truth for pending/completed; rest are
      // refined client-side from the same dataset).
      const allPending = await Tasks.list({
        type: "ACCOUNTS_INVOICE", status: "pending",
        location_ids: locFilter === "all" ? locParam : locFilter,
        with_sla: "true",
      });
      const allCompleted = await Tasks.list({
        type: "ACCOUNTS_INVOICE", status: "completed",
        location_ids: locFilter === "all" ? locParam : locFilter,
      });
      // Transit-hold count — sales-level, independent of task queue.
      const transitSales = await Sales.list({
        transit_only: true,
        location_ids: locFilter === "all" ? locParam : locFilter,
      });
      const onHold   = allPending.filter((t) => t.sla_paused).length;
      const active   = allPending.filter((t) => !t.sla_paused).length;
      const delayed  = allPending.filter((t) => (t.sla?.status === "delayed")).length;
      setTabCounts({
        pending: allPending.length,
        active,
        on_hold: onHold,
        delayed,
        transit_hold: transitSales.length,
        completed: allCompleted.length,
      });
    })();
    return () => { cancelled = true; };
  }, [statusTab, dateChip, locFilter, locParam, search, buyMode, slaFilter, filters]);

  const visibleLocs = locations.filter((l) => activeLocationIds.includes(l.id));
  const oldestPending = useMemo(() => {
    if (statusTab !== "pending") return null;
    const all = rows.filter((r) => r.task.status === "pending");
    if (!all.length) return null;
    const oldest = all.reduce((acc, r) =>
      new Date(r.task.created_at) < new Date(acc.task.created_at) ? r : acc, all[0]);
    return fmtAge(oldest.task.created_at);
  }, [rows, statusTab]);
  const latestCompletedToday = useMemo(() => {
    const td = today();
    const tdayDone = rows.filter((r) =>
      r.task.status === "completed" &&
      (r.task.completed_at || "").slice(0, 10) === td);
    if (!tdayDone.length) return null;
    const latest = tdayDone.reduce((acc, r) =>
      new Date(r.task.completed_at) > new Date(acc.task.completed_at) ? r : acc, tdayDone[0]);
    return new Date(latest.task.completed_at)
      .toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  }, [rows]);

  const onResetFilters = () => {
    setSearch(""); setLocFilter("all"); setBuyMode("all");
    setSlaFilter("all"); setDateChip("all");
  };

  const financeName = (id) => financeMasters.find((f) => f.id === id)?.name || "—";

  // ─── render ───
  if (section === "early_delivery") {
    return (
      <div className="space-y-3" data-testid="accounts-queue-page">
        <button
          type="button"
          onClick={() => setSection("invoices")}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900"
          data-testid="accounts-back-to-invoices"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Invoice Workqueue
        </button>
        <EarlyDeliveryApprovalsPanel onCountChange={setEdPendingCount} />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="accounts-queue-page">
      {/* ───────── Top header ───────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            Accounts Team
          </div>
          <h1 className="mt-0.5 font-display text-2xl font-bold tracking-tight text-slate-900">
            Accounts Workqueue
          </h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Financial checkpoint before Back Office. Verify payments, finance DO,
            disbursement and invoice processing.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setSection("early_delivery")}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            data-testid="accounts-ed-badge"
          >
            <Clock className="h-4 w-4 text-slate-500" />
            Early Delivery Approvals
            <span className={cn(
              "inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-xs font-bold",
              edPendingCount > 0 ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600",
            )}>
              {edPendingCount}
            </span>
          </button>
        </div>
      </div>

      {/* ───────── KPI Cards ───────── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          tone="amber"
          label="Pending Verification"
          value={tabCounts.pending ?? stats.pending}
          subtext="Awaiting invoice processing"
          footer={oldestPending ? `Oldest pending: ${oldestPending}` : "—"}
          Icon={FileText}
          active={statusTab === "pending" && dateChip === "all"}
          onClick={() => { setStatusTab("pending"); setDateChip("all"); }}
          testid="kpi-pending"
        />
        <KpiCard
          tone="emerald"
          label="Completed Today"
          value={stats.completed_today}
          subtext="Processed today"
          footer={latestCompletedToday ? `Latest: ${latestCompletedToday}` : "—"}
          Icon={CheckCircle2}
          active={statusTab === "completed" && dateChip === "today"}
          onClick={() => { setStatusTab("completed"); setDateChip("today"); }}
          testid="kpi-completed-today"
        />
        <KpiCard
          tone="rose"
          label="Delayed Cases"
          value={tabCounts.delayed ?? stats.delayed}
          subtext="SLA breached"
          footer="—"
          Icon={AlertTriangle}
          active={statusTab === "delayed"}
          onClick={() => { setStatusTab("delayed"); setDateChip("all"); }}
          testid="kpi-delayed"
        />
        <KpiCard
          tone="indigo"
          label="Early Delivery Requests"
          value={edPendingCount}
          subtext="Awaiting approval"
          footer="Tap to review"
          Icon={Truck}
          onClick={() => setSection("early_delivery")}
          testid="kpi-early-delivery"
        />
      </div>

      {/* ───────── Filter container ───────── */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        {/* Row 1 — search + date chips + export */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search SR / Customer / Mobile / VIN / Regn / Ledger"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="accounts-search"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1 rounded-md bg-slate-100 p-1">
            {DATE_CHIPS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setDateChip(c.value)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                  dateChip === c.value
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:bg-white/60",
                )}
                data-testid={`accounts-date-${c.value}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <ExportButton
            endpoint="tasks"
            params={{
              type: "ACCOUNTS_INVOICE",
              status: ["on_hold", "active", "delayed"].includes(statusTab) ? "pending" : statusTab,
              location_ids: locFilter === "all" ? locParam : locFilter,
            }}
            prefix={statusTab === "completed" ? "Accounts_Completed" : "Accounts_Pending"}
          />
        </div>

        {/* Row 2 — dropdowns + reset */}
        <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-center">
          <FilterSelect
            value={locFilter}
            onChange={setLocFilter}
            placeholder="All Locations"
            options={[
              { value: "all", label: "All Locations" },
              ...visibleLocs.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
            ]}
            testid="filter-location"
          />
          <FilterSelect
            value={buyMode}
            onChange={setBuyMode}
            placeholder="All Buy Modes"
            options={[
              { value: "all", label: "All Buy Modes" },
              { value: "cash", label: "Cash" },
              { value: "finance", label: "Finance" },
            ]}
            testid="filter-buy-mode"
          />
          <FilterSelect
            value={statusTab}
            onChange={setStatusTab}
            placeholder="All Status"
            options={STATUS_TABS.map((t) => ({ value: t.value, label: t.label }))}
            testid="filter-status"
          />
          <FilterSelect
            value={slaFilter}
            onChange={setSlaFilter}
            placeholder="All SLA"
            options={SLA_OPTIONS}
            testid="filter-sla"
          />
          <div className="flex-1" />
          <button
            type="button"
            onClick={onResetFilters}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            data-testid="accounts-reset-filters"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
        </div>
      </div>

      {/* ───────── Status tabs (underline) ───────── */}
      <div className="border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-1">
          {STATUS_TABS.map((t) => {
            const isActive = statusTab === t.value;
            const count = tabCounts[t.value];
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setStatusTab(t.value)}
                className={cn(
                  "relative -mb-px px-4 py-2.5 text-sm font-semibold transition-colors",
                  isActive
                    ? "text-blue-600"
                    : "text-slate-500 hover:text-slate-800",
                )}
                data-testid={`accounts-tab-${t.value}`}
              >
                <span>{t.label}</span>
                {count != null && (
                  <span className={cn(
                    "ml-1.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-bold",
                    isActive ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600",
                  )}>{count}</span>
                )}
                {isActive && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-blue-600" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ───────── Workqueue rows ───────── */}
      {statusTab === "transit_hold" ? (
        <TransitHoldList
          locParam={locFilter === "all" ? locParam : locFilter}
          locMap={locMap}
          userMap={userMap}
          canRelease={activeUser?.role === "Accounts" || activeUser?.role === "Admin"}
          releaseUserId={activeUser?.id}
          testidPrefix="accounts"
          onCountChange={(n) => setTabCounts((c) => ({ ...c, transit_hold: n }))}
        />
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center">
          <CheckCircle2 className="mx-auto mb-3 h-8 w-8 text-slate-300" />
          <div className="text-sm font-semibold text-slate-700">No invoices in this view</div>
          <div className="mt-1 text-xs text-slate-500">
            Adjust filters above to see other records.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map(({ task, sale }) => (
            <WorkqueueRow
              key={task.id}
              task={task}
              sale={sale}
              loc={locMap[sale.location_id]}
              dse={userMap[sale.dse_id]}
              finance={financeName(sale.finance?.finance_company_id)}
              tab={statusTab}
              onOpen={() => navigate(`/accounts/${sale.id}`)}
              onViewSale={() => navigate(`/sales/${sale.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────── KPI Card ───────────
function KpiCard({ tone, label, value, subtext, footer, Icon, active, onClick, testid }) {
  const tones = {
    amber:   { bg: "bg-amber-50",   ring: "ring-amber-200",   icon: "bg-amber-100 text-amber-700",     ringActive: "ring-amber-400" },
    emerald: { bg: "bg-emerald-50", ring: "ring-emerald-200", icon: "bg-emerald-100 text-emerald-700", ringActive: "ring-emerald-400" },
    rose:    { bg: "bg-rose-50",    ring: "ring-rose-200",    icon: "bg-rose-100 text-rose-700",       ringActive: "ring-rose-400" },
    indigo:  { bg: "bg-indigo-50",  ring: "ring-indigo-200",  icon: "bg-indigo-100 text-indigo-700",   ringActive: "ring-indigo-400" },
  }[tone] || {};
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "group flex flex-col rounded-xl p-4 text-left transition-all ring-1 hover:-translate-y-0.5 hover:shadow-md focus:outline-none",
        tones.bg, tones.ring,
        active && `ring-2 ${tones.ringActive} shadow-sm`,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600">
            {label}
          </div>
          <div className="mt-2 font-display text-4xl font-bold leading-none tracking-tight text-slate-900">
            {value}
          </div>
          <div className="mt-1.5 text-xs text-slate-600">{subtext}</div>
        </div>
        <span className={cn("flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full", tones.icon)}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="my-3 h-px bg-slate-200/70" />
      <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" /> {footer}
        </span>
        <ChevronRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
    </button>
  );
}

// ─────────── Filter Select ───────────
function FilterSelect({ value, onChange, placeholder, options, Icon, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-[180px]" data-testid={testid}>
        <span className="inline-flex items-center gap-2 truncate">
          {Icon && <Icon className="h-3.5 w-3.5 text-slate-500" />}
          <SelectValue placeholder={placeholder} />
        </span>
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─────────── Workqueue Row (operational card) ───────────
function WorkqueueRow({ task, sale, loc, dse, finance, tab, onOpen, onViewSale }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const td = today();
  const overdue = task.status === "pending" &&
    (sale.delivery?.promised_date || "") < td &&
    !!sale.delivery?.promised_date;

  // Decide CTA per the active tab / row state
  const cta = (() => {
    if (task.status === "completed") {
      return { label: "View", tone: "slate", testid: "view-completed" };
    }
    if (task.sla_paused) return { label: "Resolve Hold", tone: "indigo", testid: "resolve-hold" };
    if (task.sla?.status === "delayed" || overdue) {
      return { label: "Take Action", tone: "rose", testid: "take-action" };
    }
    if (tab === "active") return { label: "Continue", tone: "emerald", testid: "continue" };
    return { label: "Verify Invoice", tone: "amber", testid: "verify-invoice" };
  })();
  const ctaCls = {
    amber:   "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
    rose:    "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100",
    indigo:  "border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
    slate:   "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
  }[cta.tone];

  const isFinance = (sale.finance?.buy_mode || "cash").toLowerCase() === "finance";

  return (
    <div
      className={cn(
        "group relative rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-md",
        overdue && "ring-1 ring-rose-200",
      )}
      data-testid={`accounts-row-${sale.id}`}
    >
      <div className="grid grid-cols-1 items-center gap-4 lg:grid-cols-[180px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(180px,1fr)_auto]">
        {/* SR + Created */}
        <div className="min-w-0">
          <div className="font-mono-data text-sm font-bold text-slate-900">
            {sale.sr_number || "—"}
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            Created: <span className="font-mono-data">{fmtDateTime(task.created_at)}</span>
          </div>
        </div>

        {/* Customer */}
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-900">
            {sale.customer?.name || "—"}
          </div>
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-600">
            <Phone className="h-3 w-3 text-slate-400" />
            <span className="font-mono-data">{sale.customer?.phone || "—"}</span>
          </div>
        </div>

        {/* Vehicle */}
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <Bike className="h-3.5 w-3.5 text-slate-400" />
            <span className="truncate">{sale.vehicle?.model || "—"}</span>
          </div>
          {sale.vehicle?.vin && (
            <div className="mt-1 truncate font-mono-data text-[11px] text-slate-500">
              VIN: {sale.vehicle.vin}
            </div>
          )}
        </div>

        {/* Buy Mode / Location / DSE */}
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={cn(
              "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold",
              isFinance
                ? "border-amber-200 bg-amber-50 text-amber-800"
                : "border-slate-200 bg-slate-50 text-slate-700",
            )}>
              {isFinance ? <Wallet className="h-3 w-3" /> : <IndianRupee className="h-3 w-3" />}
              {isFinance ? "Finance" : "Cash"}
            </span>
            {loc && (
              <span className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700 ring-1 ring-slate-200">
                <MapPin className="h-3 w-3 text-slate-400" />
                {loc.code}
              </span>
            )}
          </div>
          {isFinance && finance && finance !== "—" && (
            <div className="truncate text-[11px] text-slate-500">{finance}</div>
          )}
          {dse && (
            <div className="inline-flex items-center gap-1 text-[11px] text-slate-600">
              <User className="h-3 w-3 text-slate-400" />
              <span className="truncate">Assigned: {dse.name}</span>
            </div>
          )}
        </div>

        {/* Progress badges */}
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {task.status === "completed" ? (
            <BadgePill tone="emerald" Icon={CheckCircle2} label="Completed" />
          ) : task.sla_paused ? (
            <>
              <BadgePill tone="indigo" Icon={Clock} label="On Hold" />
              <BadgePill tone="indigo-soft" label={
                (task.sla_pause_reason || "")
                  .replace(/_/g, " ")
                  .replace(/(^|\s)\w/g, (m) => m.toUpperCase()) || "Pause Reason"
              } />
            </>
          ) : task.sla?.status === "delayed" || overdue ? (
            <>
              <BadgePill tone="rose" Icon={AlertOctagon} label="Delayed" />
              <BadgePill tone="rose-soft" Icon={Clock} label={
                overdue && sale.delivery?.promised_date
                  ? `Overdue: ${fmtDate(sale.delivery.promised_date)}`
                  : "SLA Breached"
              } />
            </>
          ) : (
            <>
              <BadgePill tone={tab === "active" ? "emerald" : "amber"}
                label={tab === "active" ? "Active" : "Pending Verification"} />
              <BadgePill tone="blue-soft" Icon={Clock} label={
                task.sla?.remaining_minutes != null
                  ? `SLA: ${fmtAge(new Date(Date.now() + (task.sla.remaining_minutes || 0) * 60000).toISOString())} left`
                  : "SLA Running"
              } />
            </>
          )}
          <BadgePill tone="slate-soft" Icon={Clock} label={`Age: ${fmtAge(task.created_at)}`} />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
              onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
              data-testid={`accounts-menu-${sale.id}`}
              aria-label="More actions"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 z-20 mt-1 w-36 rounded-md border border-slate-200 bg-white py-1 shadow-md">
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); onViewSale(); }}
                  className="block w-full px-3 py-1.5 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid={`accounts-menu-view-${sale.id}`}
                >
                  View Sale Detail
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onOpen}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-3.5 py-2 text-sm font-semibold transition-colors",
              ctaCls,
            )}
            data-testid={`accounts-${cta.testid}-${sale.id}`}
          >
            {cta.label} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────── Badge Pill ───────────
function BadgePill({ tone, Icon, label }) {
  const tones = {
    amber:        "border-amber-200 bg-amber-50 text-amber-800",
    emerald:      "border-emerald-200 bg-emerald-50 text-emerald-700",
    rose:         "border-rose-200 bg-rose-50 text-rose-700",
    "rose-soft":  "border-rose-100 bg-rose-50/60 text-rose-600",
    indigo:       "border-indigo-200 bg-indigo-50 text-indigo-700",
    "indigo-soft":"border-indigo-100 bg-indigo-50/60 text-indigo-600",
    "blue-soft":  "border-blue-100 bg-blue-50/60 text-blue-700",
    "slate-soft": "border-slate-200 bg-slate-50 text-slate-600",
  }[tone] || "border-slate-200 bg-slate-50 text-slate-600";
  return (
    <span className={cn(
      "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap",
      tones,
    )}>
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </span>
  );
}
